import { type ReactNode, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Dialog } from '../../../components/ui/dialog';
import { errorMessage } from '../../../lib/error-message';
import { PreviewAction } from '../../my-files/preview/preview-frame';
import { documentApi } from '../api';

/**
 * Nút "Xoá tài liệu" — APM-PDOC-c. Chỉ đặt ở chỗ người xem có `document:approve`
 * (PM, quản trị) — cùng cửa với `DELETE /documents/:id`.
 *
 * Hỏi lại trước khi xoá, và câu hỏi nói rằng tài liệu **lấy lại được** ở tab
 * Thay đổi (dòng "đã xoá tài liệu" có nút Khôi phục) — như `DeleteTaskButton`:
 * không nói điều đó thì người ta ngần ngại xoá cả những bản đáng xoá.
 */
export function DeleteDocumentButton({
  document,
  projectId,
  onDeleted,
  inPreview = false,
}: {
  readonly document: { readonly id: string; readonly title: string };
  readonly projectId: string;
  readonly onDeleted?: () => void;
  /** Đặt trên thanh nút của khung xem trước — cùng kiểu với các nút cạnh nó. */
  readonly inPreview?: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);

  const remove = useMutation({
    mutationFn: () => documentApi.remove(document.id),
    onSuccess: () => {
      toast.success(t('documents.deleted', { title: document.title }));
      setAsking(false);
      void queryClient.invalidateQueries({ queryKey: ['document'] });
      // Dòng "đã xoá tài liệu" — nơi lấy lại — phải hiện ngay ở tab Thay đổi.
      void queryClient.invalidateQueries({ queryKey: ['project-activity', projectId] });
      onDeleted?.();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const title = t('documents.deleteTitle', { title: document.title });
  return (
    <>
      {inPreview ? (
        <PreviewAction
          label={t('documents.delete')}
          icon={<Trash2 aria-hidden />}
          destructive
          onClick={() => setAsking(true)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setAsking(true)}
          aria-label={t('documents.delete')}
          title={t('documents.delete')}
          className="glass-hover rounded-full p-1 text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" aria-hidden />
        </button>
      )}

      {asking ? (
        <Dialog label={title} onClose={() => setAsking(false)}>
          <div className="flex flex-col gap-3">
            <h2 className="text-sm font-medium">{title}</h2>
            <p className="text-sm text-muted-foreground">{t('documents.deleteBody')}</p>
            <div className="flex items-center gap-2">
              <Button
                variant="destructive"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                {t('documents.deleteConfirm')}
              </Button>
              <Button variant="ghost" onClick={() => setAsking(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </>
  );
}
